/**
 * AnimatedNumber — animates a numeric value from previous to current
 * using Motion's spring physics. Triggers on mount or when value changes.
 *
 * Usage:
 *   <AnimatedNumber value={475} />
 *   <AnimatedNumber value={5257} prefix="₺" decimals={0} />
 *   <AnimatedNumber value={44.76} decimals={4} suffix=" TL" />
 */
import { useEffect, useRef, useState } from 'react'
import { useInView, useSpring, useTransform, motion } from 'motion/react'

interface AnimatedNumberProps {
  value: number
  /** Number of decimal places (default: 0) */
  decimals?: number
  /** Prefix string (e.g. "₺", "$") */
  prefix?: string
  /** Suffix string (e.g. " TL", "%") */
  suffix?: string
  /** Locale for number formatting (default: "tr-TR") */
  locale?: string
  /** Spring duration in seconds (default: 0.8) */
  duration?: number
  /** Additional CSS class */
  className?: string
  /** Only animate when scrolled into view (default: true) */
  animateOnView?: boolean
}

export function AnimatedNumber({
  value,
  decimals = 0,
  prefix = '',
  suffix = '',
  locale = 'tr-TR',
  duration = 0.8,
  className = '',
  animateOnView = true,
}: AnimatedNumberProps) {
  const ref = useRef<HTMLSpanElement>(null)
  const isInView = useInView(ref, { once: true, amount: 0.3 })
  const [hasAnimated, setHasAnimated] = useState(false)

  // Spring from 0 to target value
  const spring = useSpring(0, {
    duration: duration * 1000,
    bounce: 0,
  })

  // Format the animated value
  const display = useTransform(spring, (current) => {
    const formatted = current.toLocaleString(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })
    return `${prefix}${formatted}${suffix}`
  })

  useEffect(() => {
    if (animateOnView && !isInView) return
    if (!hasAnimated) {
      // First animation: 0 → value
      spring.set(value)
      setHasAnimated(true)
    } else {
      // Subsequent updates: animate to new value
      spring.set(value)
    }
  }, [value, isInView, animateOnView, hasAnimated, spring])

  return (
    <motion.span ref={ref} className={className}>
      {display}
    </motion.span>
  )
}
